/* eslint-disable max-lines-per-function */
import React from "react";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { Grid, Typography } from "@mui/material";
import * as y from "yup";
import { HistoryBackButton } from "components/buttons";
import { SubmitButton } from "components/haven-design/form";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import { DatePickerField } from "components/haven-design/form/inputs";
import { AdminFormLayout } from "../shared/admin-form";

export type SubscriptionForm = {
  propertyId?: string;
  interestListId?: string;
  planId?: string;
  pricingId?: string;
  billingFrequencyId?: number | null;
  startDate?: Date | null;
  endDate?: Date | null;
  renewalDate?: Date | null;
}

type Option = {
  value: string | number;
  label: string;
}

type Props = {
  initialData: SubscriptionForm;
  formType: "update" | "new";
  handleSubmit: (
    values: SubscriptionForm,
    formikHelpers: Pick<FormikHelpers<SubscriptionForm>, "setErrors" | "setTouched">,
  ) => void | Promise<void>;
  sending: boolean;
  propertyOptions: Option[];
  interestListOptions: Option[];
  planOptions: Option[];
  pricingOptions: Option[];
  frequencyOptions: Option[];
}

export const subscriptionSchema = y
  .object()
  .shape({
    __typename: y.string().strip(true),
    id: y.string().strip(true),

    propertyId: y.string().required("Property is required"),

    interestListId: y.string().required("Interest list is required"),

    planId: y.string().required("Plan is required"),

    pricingId: y.string().required("Pricing is required"),

    billingFrequencyId: y
      .number()
      .nullable()
      .required("Billing frequency is required"),

    startDate: y
      .date()
      .required("Start date is required")
      .test(
        "start-before-end",
        "Start date must be on or before end date",
        (start, ctx) => {
          const end = ctx.parent.endDate;
          if (!(start instanceof Date) || !(end instanceof Date)) {
            return true;
          }
          return start.getTime() <= end.getTime();
        },
      ),
    endDate: y
      .date()
      .required("End date is required")
      .test(
        "end-after-start",
        "End date must be on or after start date",
        (end, ctx) => {
          const start = ctx.parent.startDate;
          if (!(start instanceof Date) || !(end instanceof Date)) {
            return true;
          }
          return end.getTime() >= start.getTime();
        },
      ),
    renewalDate: y
      .date()
      .nullable()
      .test(
        "renewal-within-term",
        "Renewal date must be on or after start date and on or before end date",
        (renewal, ctx) => {
          if (!(renewal instanceof Date)) {
            return true;
          }
          const { startDate, endDate } = ctx.parent;
          if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
            return true;
          }
          const renewalTime = renewal.getTime();
          return (
            renewalTime >= startDate.getTime() && renewalTime <= endDate.getTime()
          );
        },
      ),
  });

type SubscriptionFormContentProps = Pick<
Props,
| "propertyOptions"
| "interestListOptions"
| "planOptions"
| "pricingOptions"
| "frequencyOptions"
> & { isSubmitting: boolean; sending: boolean };

// eslint-disable-next-line max-lines-per-function -- field grid kept together for readability
const SubscriptionFormContent: React.FC<SubscriptionFormContentProps> = ({
  isSubmitting,
  sending,
  propertyOptions,
  interestListOptions,
  planOptions,
  pricingOptions,
  frequencyOptions,
}) => {
  const formik = useFormikContext<SubscriptionForm>();
  const validateSubscriptionDates = React.useCallback(() => {
    Promise.all([
      formik.validateField("startDate"),
      formik.validateField("endDate"),
      formik.validateField("renewalDate"),
    ]).catch(() => {});
  }, [formik]);

  return (
    <Form>
      <Grid container spacing={2} className="card" sx={{ mb: "30px" }}>
        <Typography>
          Create or update a subscription. Select a property and interest list; the
          property–interest list link is created automatically if it does not exist.
          Start date may be in the past or future (Invoiced subscriptions accept a
          scheduled start_date timestamp). Date ranges must not overlap another
          non-archived subscription for the same property and interest list.
        </Typography>

        <Grid size={{ xs: 12, sm: 8, md: 6 }}>
          <MaterialFormikSelectAutocomplete
            id="propertyId"
            margin="none"
            name="propertyId"
            placeholder="Property"
            title="Property"
            options={propertyOptions}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 8, md: 6 }}>
          <MaterialFormikSelectAutocomplete
            id="interestListId"
            margin="none"
            name="interestListId"
            placeholder="Interest list"
            title="Interest list"
            options={interestListOptions}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 8, md: 6 }}>
          <MaterialFormikSelectAutocomplete
            id="planId"
            margin="none"
            name="planId"
            placeholder="Plan"
            title="Plan"
            options={planOptions}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 8, md: 6 }}>
          <MaterialFormikSelectAutocomplete
            id="pricingId"
            margin="none"
            name="pricingId"
            placeholder="Pricing"
            title="Pricing"
            options={pricingOptions}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 8, md: 6 }}>
          <MaterialFormikSelectAutocomplete
            id="billingFrequencyId"
            margin="none"
            name="billingFrequencyId"
            placeholder="Billing frequency"
            title="Billing frequency"
            options={frequencyOptions}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <DatePickerField
            margin="none"
            id="startDate"
            name="startDate"
            label="Start date *"
            fullWidth
            small
            withError
            afterBlur={validateSubscriptionDates}
            afterValueChange={validateSubscriptionDates}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DatePickerField
            margin="none"
            id="endDate"
            name="endDate"
            label="End date *"
            fullWidth
            small
            withError
            afterBlur={validateSubscriptionDates}
            afterValueChange={validateSubscriptionDates}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DatePickerField
            margin="none"
            id="renewalDate"
            name="renewalDate"
            label="Renewal date"
            fullWidth
            small
            withError
            afterBlur={validateSubscriptionDates}
            afterValueChange={validateSubscriptionDates}
          />
        </Grid>
      </Grid>

      <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
        <HistoryBackButton marginRight size="L" />
        <SubmitButton
          isWide
          text="Submit"
          loadingText="Sending"
          isLoading={isSubmitting || sending}
        />
      </Grid>
    </Form>
  );
};

const SubscriptionFormComponent: React.FC<Props> = (props: Props) => (
  <AdminFormLayout>
    <Formik
      initialValues={props.initialData}
      validationSchema={subscriptionSchema}
      validateOnBlur
      onSubmit={(values, helpers) =>
        props.handleSubmit(values, {
          setErrors: helpers.setErrors,
          setTouched: helpers.setTouched,
        })
      }
      render={({ isSubmitting }) => (
        <SubscriptionFormContent
          isSubmitting={isSubmitting}
          sending={props.sending}
          propertyOptions={props.propertyOptions}
          interestListOptions={props.interestListOptions}
          planOptions={props.planOptions}
          pricingOptions={props.pricingOptions}
          frequencyOptions={props.frequencyOptions}
        />
      )}
    />
  </AdminFormLayout>
);

export default SubscriptionFormComponent;
